import { useEffect, useRef, useState } from "react";
import { BtnPrimary } from "../components/ui";
import { supabase } from "../supabaseClient";
import { S, css } from "../theme";

function AIChat({ protocol }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Hey! I'm your TrackMyPeptide AI assistant. I can answer questions about peptide research, protocols, dosing education, and help you understand the science. What would you like to know?\n\n**Just a reminder:** I provide educational information only — not medical advice. Always consult a physician before starting any protocol.` }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const SUGGESTIONS = [
    "What is the Wolverine Stack?",
    "How do I reconstitute BPC-157?",
    "What are the best peptides for fat loss?",
    "What is IGF-1 and why does it matter?",
    "What peptides help with sleep?",
  ];

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    const newMessages = [...messages, { role: "user", content: msg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const protocolContext = protocol.length > 0
        ? `The user is currently running: ${protocol.map(p => `${p.name} (${p.dose}, ${p.route}, ${p.frequency})`).join(", ")}.`
        : "The user has no active protocol set up yet.";

      const systemPrompt = `You are an AI assistant for TrackMyPeptide.com, an educational peptide tracking platform. You provide well-researched, balanced educational information about peptides for biohacking and optimization purposes.

${protocolContext}

CRITICAL RULES:
- Always clarify you provide educational information, not medical advice
- Encourage users to consult physicians before starting protocols  
- Be factual and cite uncertainty where it exists
- Never recommend specific sources or vendors
- Keep answers concise but thorough
- Use markdown formatting (bold, bullets) for clarity
- Acknowledge when research is limited or mixed`;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || "";

      const response = await fetch("/.netlify/functions/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token,
        },
        body: JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          protocolContext: protocolContext,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = errData.detail || errData.error || ("Error " + response.status);
        throw new Error(msg);
      }

      const data = await response.json();
      const reply = data.reply || "Sorry, I couldn't get a response. Please try again.";
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: "**Couldn't reach the AI.** Error detail: " + (err.message || "unknown") + "\n\nIf this mentions billing or credit, check your Anthropic account. If it mentions the API key, the key may need to be re-entered in Netlify." }]);
    }
    setLoading(false);
  }

  function formatMessage(text) {
    const lines = text.split("\n");
    let html = "";
    let inList = false;

    const inline = (s) => s
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>");

    for (let raw of lines) {
      const line = raw.trim();

      // Horizontal divider
      if (line === "---" || line === "***") {
        if (inList) { html += "</ul>"; inList = false; }
        html += '<hr style="border:none;border-top:1px solid #e5e0d8;margin:14px 0"/>';
        continue;
      }
      // Headers (###, ##, #)
      const h = line.match(/^(#{1,3})\s+(.*)/);
      if (h) {
        if (inList) { html += "</ul>"; inList = false; }
        const size = h[1].length === 1 ? "1.05rem" : h[1].length === 2 ? "0.98rem" : "0.92rem";
        html += `<div style="font-family:'Fraunces',serif;font-weight:700;font-size:${size};color:#f4ede0;margin:14px 0 6px">${inline(h[2])}</div>`;
        continue;
      }
      // Bullets (- or *)
      const b = line.match(/^[-*]\s+(.*)/);
      if (b) {
        if (!inList) { html += '<ul style="margin:6px 0;padding-left:20px">'; inList = true; }
        html += `<li style="margin:3px 0">${inline(b[1])}</li>`;
        continue;
      }
      // Numbered list item
      const n = line.match(/^(\d+)\.\s+(.*)/);
      if (n) {
        if (inList) { html += "</ul>"; inList = false; }
        html += `<div style="margin:4px 0;padding-left:4px"><strong>${n[1]}.</strong> ${inline(n[2])}</div>`;
        continue;
      }
      // Blank line
      if (line === "") {
        if (inList) { html += "</ul>"; inList = false; }
        html += '<div style="height:8px"></div>';
        continue;
      }
      // Normal paragraph
      if (inList) { html += "</ul>"; inList = false; }
      html += `<div style="margin:2px 0">${inline(line)}</div>`;
    }
    if (inList) html += "</ul>";
    return html;
  }

  return (
    <div style={{ padding: 40, display: "flex", flexDirection: "column", height: "calc(100vh - 0px)" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", margin: 0 }}>AI Guidance</h2>
        <p style={{ color: S.muted, fontSize: "0.85rem", marginTop: 6 }}>Research-backed answers, available 24/7. Educational use only.</p>
      </div>

      {messages.length === 1 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => sendMessage(s)} style={{ padding: "8px 14px", borderRadius: 100, border: `1px solid ${S.border}`, background: S.white, fontSize: "0.8rem", color: S.ink2, cursor: "pointer", fontFamily: "'Inter', sans-serif", fontWeight: 500, transition: "all 0.2s" }}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, marginBottom: 20, paddingRight: 4 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 12 }}>
            {m.role === "assistant" && (
              <div style={{ width: 36, height: 36, borderRadius: 10, background: S.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", flexShrink: 0, alignSelf: "flex-start", marginTop: 2 }}>🤖</div>
            )}
            <div style={{ maxWidth: "72%", padding: "14px 18px", borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: m.role === "user" ? S.accent : S.white, color: m.role === "user" ? "#fff" : S.ink, fontSize: "0.88rem", lineHeight: 1.7, border: m.role === "assistant" ? `1px solid ${S.border}` : "none" }}
              dangerouslySetInnerHTML={{ __html: formatMessage(m.content) }} />
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: S.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>🤖</div>
            <div style={{ padding: "14px 18px", background: S.white, border: `1px solid ${S.border}`, borderRadius: "18px 18px 18px 4px", display: "flex", gap: 6, alignItems: "center" }}>
              {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: S.muted, animation: `bounce 1.2s ${i * 0.2}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && sendMessage()}
          placeholder="Ask anything about peptides..."
          style={{ flex: 1, padding: "14px 20px", border: `1.5px solid ${S.border}`, borderRadius: 100, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.white, outline: "none", color: S.ink }}
        />
        <BtnPrimary onClick={() => sendMessage()} disabled={!input.trim() || loading}>Send →</BtnPrimary>
      </div>

      <style>{`@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }`}</style>
    </div>
  );
}

// ─── FDA UPDATES (AI-powered live) ───────────────────────────

export { AIChat };
